'use client'

import { useEffect, useState } from 'react'
import { createContext } from 'react'

import {
  GOLD_FEATURES,
  LIMITED_FEATURES,
  PLAN_DISCOUNT_CODES,
  PRICING_PLAN_AMOUNTS,
  PRICING_PLAN_DISCOUNT_PERCENTAGES,
  PRICING_PLAN_SAVINGS_AMOUNTS,
  THIRTY_PERCENT_OFF_QUARTERLY,
  THIRTY_PERCENT_OFF_QUARTERLY_TOTAL,
  UNLIMITED_FEATURES,
} from '@canyon/constants/pricing-plans'
import { Badge } from '@canyon/ui/Badge'
import { Button } from '@canyon/ui/Button'
import { Callout } from '@canyon/ui/Callout'
import { Card, CardContent } from '@canyon/ui/Card'
import { Dialog, DialogContent } from '@canyon/ui/Dialog'
import { Loader } from '@canyon/ui/Loader'
import {
  SwitchTabs,
  SwitchTabsContent,
  SwitchTabsList,
  SwitchTabsTrigger,
} from '@canyon/ui/SwitchTabs'
import { Tag } from '@canyon/ui/Tag'
import { cn } from '@canyon/ui/utils/cn'
import {
  RiArrowUpCircleLine,
  RiCheckboxCircleLine,
  RiSparklingFill,
  RiTimerFlashLine,
} from '@remixicon/react'
import { motion } from 'framer-motion'

import { SubscriptionPlanEnum } from '@gql/graphql'

import { PricingTable } from '@/components/PricingTable'
import { ProBadge } from '@/components/ProBadge'
import { useBillingPlan } from '@/hooks/useBillingPlan'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { trackEvent } from '@/lib/utils/analytics'
import { TBillingPlanFrequency } from '@/types/pricing'

interface IUpgradePlanDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
}

const EVENT_PREFIX = 'Pricing Plan Dialog'

const UpgradePlanDialogContext = createContext<
  { open: boolean; setOpen: (open: boolean) => void } | undefined
>(undefined)

export const UpgradePlanDialog = ({ open, setOpen }: IUpgradePlanDialogProps) => {
  const [pricingPlanTab, setPricingPlanTab] = useState<TBillingPlanFrequency>('quarterly')

  const {
    loading: currentUserIsLoading,
    timeLeftForWelcomeOffer,
    userAccountIsLessThanWeekOld,
  } = useCurrentUser()

  const handleBillingFrequencySwitch = (value: TBillingPlanFrequency) => {
    trackEvent(`${EVENT_PREFIX} - Toggled Billing Frequency`, { frequencyTo: value })
    setPricingPlanTab(value)
  }

  useEffect(() => {
    if (open) {
      trackEvent(`${EVENT_PREFIX} - Opened`)
    }
  }, [])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      trackEvent(`${EVENT_PREFIX} - Closed`)
    }

    setOpen(open)
  }

  const quarterlyDiscountPercentage = userAccountIsLessThanWeekOld
    ? PRICING_PLAN_DISCOUNT_PERCENTAGES[THIRTY_PERCENT_OFF_QUARTERLY]
    : PRICING_PLAN_DISCOUNT_PERCENTAGES.quarterly

  return (
    <UpgradePlanDialogContext.Provider value={{ open, setOpen }}>
      <Dialog onOpenChange={handleOpenChange} open={open}>
        <DialogContent
          className="max-h-[90vh] max-w-[1000px] [&>div]:bg-background"
          title="Upgrade Plan"
          titleIcon={<RiArrowUpCircleLine size={14} />}
        >
          {currentUserIsLoading ? (
            <Loader />
          ) : (
            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <SwitchTabs
                className="flex w-full flex-col items-center justify-center self-center p-4 pb-8"
                defaultValue="quarterly"
                onValueChange={handleBillingFrequencySwitch}
                value={pricingPlanTab}
              >
                <SwitchTabsList>
                  <SwitchTabsTrigger className="flex flex-row gap-2" value="quarterly">
                    Quarterly{' '}
                    <Badge className="px-1 py-0" variant="discount">
                      -{quarterlyDiscountPercentage}%
                    </Badge>
                  </SwitchTabsTrigger>
                  <SwitchTabsTrigger value="monthly">Monthly</SwitchTabsTrigger>
                </SwitchTabsList>
                <div className="mt-1 flex w-full flex-col gap-2">
                  {userAccountIsLessThanWeekOld ? (
                    <Callout
                      icon={RiTimerFlashLine}
                      title={`Limited Time Welcome Offer (${timeLeftForWelcomeOffer} days left)`}
                      variant="success"
                    >
                      Get 40% off your first 3 months of Pro and Gold Quarterly plans when you join
                      within 7 days.
                    </Callout>
                  ) : (
                    <Callout
                      icon={<RiSparklingFill className="text-yellow-300" size={16} />}
                      title="Supercharge Your Job Search"
                      variant="purple"
                    >
                      Join thousands of Pro and Gold users who are leveraging Canyon to land more
                      interviews at top companies.
                    </Callout>
                  )}
                  <SwitchTabsContent value="quarterly">
                    <PricingContent selectedPricing={pricingPlanTab} />
                  </SwitchTabsContent>
                  <SwitchTabsContent value="monthly">
                    <PricingContent selectedPricing={pricingPlanTab} />
                  </SwitchTabsContent>
                </div>
              </SwitchTabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </UpgradePlanDialogContext.Provider>
  )
}

const PricingContent = ({ selectedPricing }: { selectedPricing: TBillingPlanFrequency }) => {
  const { user } = useCurrentUser()
  const [showFeatureTable, setShowFeatureTable] = useState(false)

  return (
    <div className="space-y-6">
      <PricingPlanCards selectedPricing={selectedPricing} />
      <div className="space-y-3">
        <div className="w-full text-center">
          <button
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
            onClick={() => setShowFeatureTable(!showFeatureTable)}
            type="button"
          >
            {showFeatureTable ? 'Hide' : 'Show'} Feature Comparison
          </button>
        </div>
        {showFeatureTable && (
          <PricingTable className="mt-4" selectedPricing={selectedPricing} user={user} />
        )}
      </div>
    </div>
  )
}

const PricingPlanCards = ({ selectedPricing }: { selectedPricing: TBillingPlanFrequency }) => {
  const [isCreateSubscriptionLoading, setIsCreateSubscriptionLoading] = useState<boolean>(false)
  const { createSubscription } = useBillingPlan()
  const { isPaidPlan, userAccountIsLessThanWeekOld } = useCurrentUser()

  const handleUpgradePlan = ({ plan }: { plan: 'gold' | 'pro' }) => {
    setIsCreateSubscriptionLoading(true)

    trackEvent(`${EVENT_PREFIX} - clicked Upgraded to Pro`, { frequency: selectedPricing })

    if (plan === 'gold') {
      return createSubscription({
        coupon:
          userAccountIsLessThanWeekOld && selectedPricing === 'quarterly'
            ? PLAN_DISCOUNT_CODES['30pctOffGold']
            : undefined,
        plan:
          selectedPricing === 'quarterly'
            ? SubscriptionPlanEnum.GoldQuarterly
            : SubscriptionPlanEnum.Gold,
      })
    }

    return createSubscription({
      coupon:
        userAccountIsLessThanWeekOld && selectedPricing === 'quarterly'
          ? PLAN_DISCOUNT_CODES['30pctOffPro']
          : undefined,
      plan:
        selectedPricing === 'quarterly'
          ? SubscriptionPlanEnum.ProQuarterly
          : SubscriptionPlanEnum.Pro,
    })
  }

  return (
    <div className="flex w-full flex-row justify-center gap-3">
      <PricingPlanCard featuresList={LIMITED_FEATURES} pricePerMonth="0" title="Starter" />
      <PricingPlanCard
        button={
          isPaidPlan ? (
            <Button
              disabled={true}
              fullWidth={true}
              loading={isCreateSubscriptionLoading}
              size="lg"
              variant="ctaBlue"
            >
              Already on Pro Plan
            </Button>
          ) : (
            <Button
              fullWidth={true}
              loading={isCreateSubscriptionLoading}
              onClick={() => handleUpgradePlan({ plan: 'pro' })}
              size="lg"
              variant="ctaBlue"
            >
              Upgrade to Pro
            </Button>
          )
        }
        discountedPricePerMonth={
          userAccountIsLessThanWeekOld
            ? PRICING_PLAN_AMOUNTS.pro[THIRTY_PERCENT_OFF_QUARTERLY]
            : PRICING_PLAN_AMOUNTS.pro.quarterly
        }
        featuresList={UNLIMITED_FEATURES}
        isHighlighted={true}
        pricePerMonth={PRICING_PLAN_AMOUNTS.pro.monthly}
        savingsAmount={
          userAccountIsLessThanWeekOld
            ? PRICING_PLAN_SAVINGS_AMOUNTS.pro[THIRTY_PERCENT_OFF_QUARTERLY]
            : PRICING_PLAN_SAVINGS_AMOUNTS.pro.quarterly
        }
        selectedPricing={selectedPricing}
        title="Pro"
        totalPrice={
          userAccountIsLessThanWeekOld
            ? PRICING_PLAN_AMOUNTS.pro[THIRTY_PERCENT_OFF_QUARTERLY_TOTAL]
            : PRICING_PLAN_AMOUNTS.pro.quarterlyTotal
        }
      />
      <PricingPlanCard
        button={
          <Button
            className="bg-orange-500 text-orange-100 hover:bg-orange-600"
            fullWidth={true}
            loading={isCreateSubscriptionLoading}
            onClick={() => handleUpgradePlan({ plan: 'gold' })}
            size="lg"
          >
            Upgrade to Gold
          </Button>
        }
        discountedPricePerMonth={
          userAccountIsLessThanWeekOld
            ? PRICING_PLAN_AMOUNTS.gold[THIRTY_PERCENT_OFF_QUARTERLY]
            : PRICING_PLAN_AMOUNTS.gold.quarterly
        }
        featuresList={GOLD_FEATURES}
        pricePerMonth={PRICING_PLAN_AMOUNTS.gold.monthly}
        savingsAmount={
          userAccountIsLessThanWeekOld
            ? PRICING_PLAN_SAVINGS_AMOUNTS.gold[THIRTY_PERCENT_OFF_QUARTERLY]
            : PRICING_PLAN_SAVINGS_AMOUNTS.gold.quarterly
        }
        selectedPricing={selectedPricing}
        title="Gold"
        totalPrice={
          userAccountIsLessThanWeekOld
            ? PRICING_PLAN_AMOUNTS.gold[THIRTY_PERCENT_OFF_QUARTERLY_TOTAL]
            : PRICING_PLAN_AMOUNTS.gold.quarterlyTotal
        }
      />
    </div>
  )
}

interface IPricingPlanCardProps {
  button?: React.ReactNode
  discountedPricePerMonth?: null | string
  featuresList?: string[]
  isHighlighted?: boolean
  pricePerMonth: null | string
  savingsAmount?: number
  selectedPricing?: string
  title: React.ReactNode | string
  totalPrice?: null | string
}

const PricingPlanCard = ({
  button,
  discountedPricePerMonth,
  featuresList,
  isHighlighted,
  pricePerMonth,
  savingsAmount,
  selectedPricing,
  title,
  totalPrice,
}: IPricingPlanCardProps) => {
  const hasSelectedQuarterlyPricing = selectedPricing === 'quarterly'
  const shouldShowDiscountedPrice = discountedPricePerMonth && hasSelectedQuarterlyPricing

  return (
    <Card className={cn('w-full', isHighlighted && 'border border-blue-500')}>
      <CardContent className="relative flex h-full flex-col gap-3 px-3 py-4">
        <div className="text-lg font-semibold">{title}</div>
        <div className="flex flex-col">
          <div className={cn('text-2xl font-semibold texet-white')}>
            <motion.span
              animate={{ opacity: 1, x: 0 }}
              className="inline"
              exit={{ opacity: 0, x: 20 }}
              initial={{ opacity: 0, x: -20 }}
              key={pricePerMonth}
              transition={{ duration: 0.5 }}
            >
              <span
                className={cn(shouldShowDiscountedPrice && 'line-through')}
              >{`$${pricePerMonth}`}</span>
              {shouldShowDiscountedPrice && (
                <span className="ml-1">{`$${discountedPricePerMonth}`}</span>
              )}
              <span className="text-muted-foreground">{`/month${hasSelectedQuarterlyPricing ? '*' : ''}`}</span>
            </motion.span>
          </div>
          {totalPrice && hasSelectedQuarterlyPricing && (
            <div className="text-muted-foreground text-sm">
              <span>{`$${totalPrice}`} total</span>
            </div>
          )}
        </div>
        {featuresList && (
          <div className="text-muted-foreground flex flex-col gap-2 text-sm">
            {featuresList?.map((feature) => (
              <div className="flex flex-row items-center gap-2">
                <RiCheckboxCircleLine size={14} /> {feature}
              </div>
            ))}
          </div>
        )}
        <div className="my-2">{button}</div>
        {hasSelectedQuarterlyPricing && savingsAmount && (
          <Badge className="absolute right-[16px] top-[16px]" variant="discount">
            Save ${savingsAmount}
          </Badge>
        )}
      </CardContent>
    </Card>
  )
}
